import { clsx } from 'clsx';
import { faCaretDown } from '@fortawesome/pro-solid-svg-icons';
import {
  alignLeft,
  alignRight,
  dropDown,
  icon,
  option,
  optionDefault,
  optionSelected,
  visible,
} from './dropDown.css';
import { Chip, ChipProps } from '../chip/chip';
import { sprinkles } from '@design-system/sprinkles.css';
import { ReactNode, useEffect, useRef, useState } from 'react';
import { IconProp } from '@fortawesome/fontawesome-svg-core';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Typography } from '../typography/typography';
import { faClose } from '@fortawesome/pro-regular-svg-icons';
import { chip, chipIcon, chipSelected, events } from '../chip/chip.css';

type OptionValue = string | number;

export type FilterOption<T extends OptionValue = OptionValue> = {
  label: string | ReactNode;
  selectedLabel?: string | ReactNode;
  value: T;
  startIcon?: IconProp;
  endIcon?: IconProp;
  endCta?: ReactNode;
  direction?: 'asc' | 'desc';
  onClick?: (v: FilterOption<T>) => void;
};

export type DropDownProps<T extends OptionValue = OptionValue> = Omit<
  ChipProps,
  'onClick'
> & {
  options: Array<FilterOption<T>>;
  value?: OptionValue;
  alightRight?: boolean;
  menuClassName?: string;
  chipClassName?: string;
  deleteSelection?: () => void;
  selectedLabel?: string | ReactNode;
  selectSkipStyle?: boolean;
  onSelect?: (v: T | undefined) => void;
};

export const DropDownFilter = <T extends OptionValue>(p: DropDownProps<T>) => {
  const [open, setOpen] = useState(false);
  const menuRef = useRef(null);

  const toogleMenu = function () {
    setOpen((prev) => !prev);
  };

  useEffect(() => {
    const close = function (ev) {
      if (!menuRef.current?.contains(ev.target)) {
        setOpen(false);
      }
    };
    document.addEventListener('click', close);

    return () => {
      document.removeEventListener('click', close);
    };
  }, []);

  const selectedOption = p?.value && p.options.find((i) => i.value === p.value);

  return (
    <div className="relative max-w-fit" ref={menuRef}>
      <div>
        {p?.value || p?.selectedLabel ? (
          <div
            className={clsx(
              chip,
              !p?.selectSkipStyle && chipSelected,
              !p?.selectSkipStyle && 'bg-black text-white',
              p?.selectSkipStyle && 'text-black bg-white',
            )}
            data-testid="chip"
          >
            <div
              onClick={(ev) => {
                ev.stopPropagation();
                toogleMenu();
              }}
              className="flex items-center"
            >
              <Typography
                variant="body"
                size="medium"
                as="span"
                className={events}
              >
                {p?.selectedLabel ||
                  selectedOption?.selectedLabel ||
                  selectedOption?.label ||
                  p?.value ||
                  ''}
              </Typography>
            </div>
            <div
              className="flex items-center"
              onClick={(ev) => {
                ev.stopPropagation();
                if (p.deleteSelection) p.deleteSelection();
                else if (p.onSelect) p.onSelect(undefined);
                setOpen(false);
              }}
            >
              <FontAwesomeIcon
                icon={faClose}
                className={clsx(chipIcon, events)}
              />
            </div>
          </div>
        ) : (
          <Chip
            {...p}
            endIcon={faCaretDown}
            selected={open}
            onClick={toogleMenu}
            className={clsx(
              p?.chipClassName,
              'bg-white hover:text-black text-black',
            )}
          />
        )}
      </div>
      <div
        className={clsx(
          dropDown,
          open && visible,
          p?.alightRight ? alignRight : alignLeft,
          p?.menuClassName,
        )}
        data-testid="dropDownFilter"
      >
        {p.options.map((item) => (
          <div
            key={item.value}
            className={clsx(
              option,
              p?.value === item.value ? optionSelected : optionDefault,
            )}
            onClick={() => {
              if (p.onSelect) p.onSelect(item.value);
              else item?.onClick(item);
              setOpen(false);
            }}
          >
            {item.startIcon ? (
              <FontAwesomeIcon icon={item.startIcon} className={icon} />
            ) : null}
            <Typography
              variant="body"
              size="large"
              as="span"
              className={clsx(sprinkles({ width: '100%' }))}
            >
              {item.label}
            </Typography>
            {item?.endIcon ? (
              <FontAwesomeIcon icon={item.endIcon} className={icon} />
            ) : null}
            {item?.endCta ? item.endCta : null}
          </div>
        ))}
      </div>
    </div>
  );
};
